<template>
  <default-layout>
    <v-row>
      <v-col cols="12" sm="12" md="6" lg="4">
        <v-form
          v-model="valid"
          ref="subscriptionPricingForm"
          id="subscriptionPricingForm"
          @submit.prevent="createSubscriptionPricing"
        >
          <v-card :loading="loading">
            <v-card-text>
              <p>A few tips for creating a pricing override:</p>
              <ul>
                <li>
                  Don't create a pricing override that overlaps another
                  override's dates.
                </li>
                <li>
                  If you want the override to never end, just set the end date
                  for in 100 years.
                </li>
                <li>
                  Create overrides that match up with their Monthly Upgrade Fee
                  start date. For example if their Monthly Upgrade Fee starts on
                  Jan 15th and you want the override to last 1 year, start the
                  override on Jan 15th, and end it on Jan 14th of the next year.
                </li>
              </ul>
              <!-- START DATE -->
              <v-row>
                <v-col cols="12">
                  <v-menu
                    ref="startDate"
                    v-model="showStartDateCalendar"
                    :close-on-content-click="false"
                    :return-value.sync="form.startDate"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.startDate"
                        label="Start Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :rules="startDateRules"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.startDate"
                      no-title
                      scrollable
                      :min="minStartDate"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="showStartDateCalendar = false"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.startDate.save(form.startDate)"
                      >
                        OK
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <!-- END DATE -->
              <v-row>
                <v-col cols="12">
                  <v-menu
                    ref="expireDate"
                    v-model="showExpireDateCalendar"
                    :close-on-content-click="false"
                    :return-value.sync="form.expireDate"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.expireDate"
                        label="Expire Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :rules="expireDateRules"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.expireDate"
                      no-title
                      scrollable
                      :min="minExpireDate"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="showexpireDateCalendar = false"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.expireDate.save(form.expireDate)"
                      >
                        OK
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <!-- OVERRIDE AMOUNT -->
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    label="Override amount"
                    type="number"
                    prepend-icon="mdi-currency-usd"
                    v-model="form.override"
                    :rules="overrideRules"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-btn
                class="mr-4"
                type="submit"
                color="primary"
                :loading="loading"
              >
                submit
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-col>
    </v-row>
  </default-layout>
</template>

<script>
import { startOfToday, format, parseISO, isBefore } from "date-fns";
import { createSubscriptionCostOverride } from "../../../api/subscription";
import { getFacilitySubscriptionOverrides } from "../../../api/facility";

export default {
  name: "AddSubscriptionPricingOverride",
  title: "Create Monthly Upgrade Fee Pricing Override",
  data() {
    return {
      loading: false,
      valid: false,
      showStartDateCalendar: false,
      showExpireDateCalendar: false,
      facility: null,
      form: {
        startDate: null,
        expireDate: null,
        override: null
      },
      overrides: []
    };
  },

  computed: {
    minStartDate() {
      return format(startOfToday(), "yyyy-MM-dd");
    },
    minExpireDate() {
      if (this.form.startDate) {
        return this.form.startDate;
      }
      return this.minStartDate;
    },

    startDateRules() {
      return [
        v => !!v || "Start date is required",
        v =>
          !isBefore(parseISO(v), startOfToday()) ||
          "Start date cannot be in the past",
        v => this.checkForOverlaps(v)
      ];
    },

    expireDateRules() {
      return [
        v => !!v || "End date is required",
        v =>
          !isBefore(parseISO(v), parseISO(this.form.startDate)) ||
          "End date cannot be before the start date",
        v => this.checkForOverlaps(v)
      ];
    },

    overrideRules() {
      const overrideRules = [
        v => !!v || "Override amount is required",
        v => !isNaN(v) || "Override amount must be numeric",
        v => parseFloat(v) <= 1000 || "Override amount must be less than 1000",
        v => {
          // limit to 2 decimal places
          if (v) {
            const splitString = v.toString().split(".")[1];
            if (splitString && splitString.length && splitString.length > 2) {
              return "No more than 2 decimal places are allowed.";
            }
          }
          return true;
        }
      ];

      return overrideRules;
    }
  },

  async mounted() {
    this.loading = true;

    this.facilityId = parseInt(this.$route.params.facilityId);
    if (this.facilityId) {
      const result = await getFacilitySubscriptionOverrides(
        this.$route.params.facilityId
      );
      this.overrides = result.items;
    }

    this.loading = false;
  },

  methods: {
    checkForOverlaps(v) {
      const overlap = this.overrides.find(override => {
        const startDate = parseISO(override.startDate);
        const expireDate = parseISO(override.expireDate);
        const newExpireDate = parseISO(v);

        return startDate <= newExpireDate < expireDate;
      });

      if (overlap) {
        return `This start date overlaps with an override of $${
          overlap.overrides
        } starting on ${format(
          parseISO(overlap.startDate),
          "MM/dd/yyyy"
        )} and ending on ${format(
          parseISO(overlap.expireDate),
          "MM/dd/yyyy"
        )}. Please select a date outside this range.`;
      }

      return true;
    },
    async createSubscriptionPricing() {
      const valid = this.$refs.subscriptionPricingForm.validate();
      if (!valid) {
        console.error("THE FORM IS NOT VALID");
        return;
      }

      this.loading = true;
      try {
        const startDate = parseISO(this.form.startDate);
        const expireDate = parseISO(this.form.expireDate);

        await createSubscriptionCostOverride(
          this.facilityId,
          this.form.override,
          startDate,
          expireDate
        );

        this.$router.push({ name: "CompanyPricing" });
      } catch (e) {
        console.error(e);
      }

      this.loading = false;
    }
  }
};
</script>
